import { useEffect, useState } from 'react'
import { brandInitial } from '@shoptrack/common/brand'
import { cn } from '@/lib/utils'
import { useBrand, type BrandInfo } from '@/lib/brand'

export const SHOPTRACK_SLOGAN = 'Quản lý dễ dàng - Bán hàng vượt trội'

interface LogoProps {
  className?: string
  showText?: boolean
  /** Hiện slogan dưới chữ ShopTrack (chỉ khi showText). */
  showSlogan?: boolean
  textClassName?: string
  imgClassName?: string
  /**
   * Chỗ đặt logo — quyết định khung tối đa của logo NPP (docs/KE_HOACH_THUONG_HIEU_NPP.md §4):
   * 'bar' = thanh trên / đầu menu (mặc định), 'auth' = trang Đăng nhập / Đăng ký…
   */
  place?: 'bar' | 'auth'
  /** false = luôn logo ShopTrack (văn bản pháp lý của ShopTrack — không gắn logo NPP lên) */
  brandable?: boolean
}

/**
 * ShopTrack — chữ S tuyến giao hàng.
 * Ảnh logo-s-mark.png chỉ dùng làm mask: chữ S lấy màu chữ hiện tại (currentColor),
 * nên `text-accent`, `text-onAccent`, chế độ tối… tự đúng màu, không cần bản trắng riêng.
 * NPP có thương hiệu riêng → logo + tên NPP thay ShopTrack (BrandLogo).
 */
export function ShopTrackLogo({ className, showText = true, showSlogan = false, textClassName, imgClassName, place = 'bar', brandable = true }: LogoProps) {
  const current = useBrand()
  const brand = brandable ? current : null
  const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')
  const mask = `url(${base}/logo-s-mark.png) center / contain no-repeat`

  if (brand) return <BrandLogo brand={brand} showText={showText} place={place} className={className} textClassName={textClassName} />

  return (
    <div className={cn("inline-flex items-center gap-2.5 select-none cursor-pointer", className)}>
      <span
        role="img"
        aria-label="ShopTrack"
        className={cn("block h-7 aspect-square shrink-0 max-h-[2.25rem] bg-current transition-all", imgClassName)}
        style={{ WebkitMask: mask, mask }}
      />
      {showText && (
        <span className="flex flex-col">
          <span className={cn("font-bold tracking-tight leading-none", textClassName)}>
            Shop<span className="opacity-80">Track</span>
          </span>
          {showSlogan && (
            <span className="mt-1.5 text-xs font-medium leading-none text-inkSoft">{SHOPTRACK_SLOGAN}</span>
          )}
        </span>
      )}
    </div>
  )
}

// Khung cố định của logo NPP — logo co vừa khung, giữ tỉ lệ, không bao giờ to hơn (§4).
// Máy tính: thanh 32 × ≤160, đăng nhập 48 × ≤220; điện thoại: thanh 28 × ≤120, đăng nhập 40 × ≤180;
// biểu tượng nhỏ chỉ có logo ngang: 28 × ≤88.
const WIDE_BOX = {
  bar: 'h-8 max-w-[160px] mobile:h-7 mobile:max-w-[120px]',
  auth: 'h-12 max-w-[220px] mobile:h-10 mobile:max-w-[180px]',
} as const
// Chỉ có logo ngang mà cần biểu tượng nhỏ (thanh trên điện thoại, cạnh tên shop): khung hẹp hơn để tên shop còn chỗ
const WIDE_MARK_BOX = {
  bar: 'h-7 max-w-[88px]',
  auth: 'h-10 max-w-[120px]',
} as const
const MARK_BOX = {
  bar: 'size-8 mobile:size-7',
  auth: 'size-12 mobile:size-10',
} as const
const NAME_TEXT = {
  bar: 'text-base max-w-[150px] mobile:max-w-[120px]',
  auth: 'text-xl max-w-[220px] mobile:max-w-[180px]',
} as const

/** Chế độ tối: logo NPP thường có chữ màu tối → đặt trên nền sáng nhỏ để luôn đọc được. */
const PLATE = 'dark:rounded-md dark:bg-white/95 dark:p-0.5'

/** Ảnh lỗi (mạng, logo vừa gỡ) → quay về chữ cái đầu, không để ô trống. */
function useImageOk(src: string | null): [boolean, () => void] {
  const [failed, setFailed] = useState<string | null>(null)
  useEffect(() => setFailed(null), [src])
  return [!!src && failed !== src, () => setFailed(src)]
}

function Initial({ name, className }: { name: string; className?: string }) {
  return (
    <span aria-hidden className={cn('grid shrink-0 place-items-center rounded-lg bg-accent font-bold leading-none text-onAccent', className)}>
      {brandInitial(name)}
    </span>
  )
}

export function BrandLogo({ brand, showText = true, place = 'bar', className, textClassName }: {
  brand: BrandInfo
  showText?: boolean
  place?: 'bar' | 'auth'
  className?: string
  textClassName?: string
}) {
  const [wideOk, wideFailed] = useImageOk(brand.logoWide)
  const [squareOk, squareFailed] = useImageOk(brand.logoSquare)
  const initialSize = place === 'auth' ? 'text-lg' : 'text-sm'
  const name = (
    <span title={brand.name} className={cn('truncate font-bold leading-tight tracking-tight text-ink', NAME_TEXT[place], textClassName)}>
      {brand.name}
    </span>
  )
  const mark = squareOk ? (
    <img src={brand.logoSquare!} alt={showText ? '' : brand.name} onError={squareFailed} draggable={false}
      className={cn('shrink-0 rounded-md object-contain', PLATE, MARK_BOX[place])} />
  ) : (
    <Initial name={brand.name} className={cn(MARK_BOX[place], initialSize)} />
  )

  // Biểu tượng nhỏ (thanh trên điện thoại, menu thu gọn): logo vuông → logo ngang trong khung thanh → chữ cái đầu
  if (!showText) {
    return (
      <span role="img" aria-label={brand.name} className={cn('inline-flex min-w-0 select-none items-center', className)}>
        {squareOk || !wideOk ? mark : (
          <img src={brand.logoWide!} alt="" onError={wideFailed} draggable={false}
            className={cn('block w-auto object-contain object-left', PLATE, WIDE_MARK_BOX[place])} />
        )}
      </span>
    )
  }

  // Có logo ngang: chỉ ảnh (tên thường đã nằm trong logo), tên đặt vào alt cho trình đọc màn hình
  if (wideOk) {
    return (
      <span className={cn('inline-flex min-w-0 select-none items-center', className)}>
        <img src={brand.logoWide!} alt={brand.name} onError={wideFailed} draggable={false}
          style={brand.logoWideRatio ? { aspectRatio: String(brand.logoWideRatio) } : undefined}
          className={cn('block w-auto object-contain object-left', PLATE, WIDE_BOX[place])} />
      </span>
    )
  }
  return (
    <span className={cn('inline-flex min-w-0 select-none items-center gap-2.5', className)}>
      {mark}
      {name}
    </span>
  )
}

/** Dòng nhỏ "Chạy trên ShopTrack" — chỉ hiện khi NPP có thương hiệu riêng. */
export function PoweredBy({ className }: { className?: string }) {
  const brand = useBrand()
  if (!brand) return null
  return <p className={cn('text-[11px] leading-4 text-muted', className)}>Chạy trên ShopTrack</p>
}
